import {Fragment, useEffect, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {parseProductHandle} from '../../lib/parseProductHandle';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from '@headlessui/react';
import {
  Bars3Icon,
  ShoppingBagIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {motion, useMotionValueEvent, useScroll} from 'motion/react';
import BrandLogo from '../brand/BrandLogo';
import {NewsFlashBanner} from '../newsflash/NewsFlashBanner';
import {Search} from '../search/SearchInput.client';
import {LazyImage} from '../elements/LazyImage';
import {Heading} from '../elements/Heading';
import {CartDetails} from '../cart/CartDetails.client';
import {useCart} from '@shopify/hydrogen';
export function Navigation({title, route, upsell, ...menu}) {
  const {scrollY} = useScroll();
  const [open, setOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [isAnyFlyoutOpen, setIsAnyFlyoutOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [prevScroll, setPrevScroll] = useState(0);
  const [active, setActive] = useState(null);

  const upsellFormat = (upsellItem) => {
    if (!upsellItem?.fields) return null;

    return upsellItem.fields.reduce((acc, field) => {
      switch (field.key) {
        case 'cta':
          acc.cta = field.value;
          break;
        case 'image':
          acc.image = field.reference.image.url;
          acc.alt = field.reference.alt;
          break;
        case 'product':
          acc.product = `/products/${field.reference.handle}`;
          break;
        case 'collection':
          acc.collection = `/collections/${field.reference.handle}`;
          break;
      }
      return acc;
    }, {});
  };

  const formatMenu = menu?.items?.map((item) => {
    const matchingUpsells = upsell?.filter((upsellItem) => {
      const menuPlacement = upsellItem.fields.find(
        (field) => field.key === 'menu_placement',
      );
      return menuPlacement?.value.toLowerCase() === item.title.toLowerCase();
    });

    if (matchingUpsells?.length > 0) {
      return {
        ...item,
        upsell: matchingUpsells.map(upsellFormat),
      };
    }
    return item;
  });

  const handleFlyoutOpen = (menuTitle) => {
    if (active === menuTitle) {
      setIsAnyFlyoutOpen(false);
      setActive(null);
    } else {
      setIsAnyFlyoutOpen(true);
      setActive(menuTitle);
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setTimeout(() => {
        setIsAnyFlyoutOpen(false);
        setActive(null);
      }, 500);
    };

    if (isAnyFlyoutOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [isAnyFlyoutOpen]);

  // Scroll params
  const parentVariants = {
    visible: {opacity: 1, y: 0},
    hidden: {opacity: 0, y: '-4rem'},
  };

  const childVariants = {
    visible: {opacity: 1, y: 0},
    hidden: {opacity: 0, y: '-2rem'},
  };

  function update(latest, prev) {
    if (latest < prev) {
      setHidden(false);
    } else if (latest > 100 && latest > prev) {
      setHidden(true);
    }
  }

  useMotionValueEvent(scrollY, 'change', (latest) => {
    update(latest, prevScroll);
    setPrevScroll(latest);
  });

  return (
    <div className={`fixed w-full z-100`}>
      {/* Announcement Bar */}
      <NewsFlashBanner isHome={route} />

      {/* Mobile Menu */}
      <Dialog open={open} onClose={setOpen} className="relative z-40 lg:hidden">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/25 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />
        <div className="fixed inset-0 z-40 flex">
          <DialogPanel
            transition
            className="relative flex w-full transform flex-col overflow-y-auto bg-white pb-12 shadow-xl transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            {/* Links */}
            <TabGroup className="mt-2">
              <div className="flex px-4 py-6 justify-end">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="relative -m-2 inline-flex items-center justify-center rounded-md p-2 text-black"
                >
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon aria-hidden="true" className="w-6 h-6" />
                </button>
              </div>
              <div className="border-b border-gray-200">
                <TabList className="-mb-px flex space-x-8 px-4">
                  {formatMenu.map((menu) => (
                    <Tab
                      key={menu.id}
                      className="flex-1 whitespace-nowrap border-b-2 border-transparent px-1 py-4 text-base font-medium text-gray-900 data-[selected]:border-black data-[selected]:text-black"
                    >
                      {menu.title}
                    </Tab>
                  ))}
                </TabList>
              </div>
              <TabPanels as={Fragment}>
                {formatMenu.map((menu) => (
                  <TabPanel
                    key={menu.id}
                    name={menu.title}
                    className="space-y-10 px-4 pb-8 pt-10"
                  >
                    <div className="grid grid-cols-2 gap-x-4">
                      {menu.upsell?.map((product) => (
                        <div
                          key={product.cta}
                          className="group relative text-xs"
                        >
                          <a
                            href={product.product || product.collection}
                            className="hover:opacity-75 transition-opacity duration-200"
                          >
                            <LazyImage
                              src={product.image}
                              width={100}
                              height={100}
                              alt={product.alt || product.cta}
                              className="aspect-square w-full object-cover bg-gray-100 hover:opacity-75 transition-opacity duration-200"
                            />
                            <div className="mt-6 block uppercase tracking-widest font-medium text-black text-center">
                              <span
                                aria-hidden="true"
                                className="absolute inset-0 z-10"
                              />
                              {product.cta}
                            </div>
                          </a>
                        </div>
                      ))}
                    </div>

                    <div
                      className={`grid ${
                        menu.items.length > 12 ? 'grid-cols-2' : 'grid-cols-1'
                      } gap-4`}
                    >
                      {menu?.items?.map((item) => (
                        <a
                          key={item.id}
                          href={
                            item.type === 'COLLECTION' ||
                            item.title === 'All Fragrances'
                              ? `/collections/${parseProductHandle(item.to)}`
                              : `/products/${parseProductHandle(item.to)}`
                          }
                          className="tracking-wider font-medium hover:text-gray-600"
                        >
                          {item.title}
                        </a>
                      ))}
                    </div>
                  </TabPanel>
                ))}
              </TabPanels>
            </TabGroup>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop Navigation */}
      <motion.header
        variants={parentVariants}
        animate={hidden ? 'hidden' : 'visible'}
        transition={{
          ease: [0.1, 0.25, 0.3, 1],
          duration: 0.6,
          staggerChildren: 0.05,
        }}
        className={`${
          isAnyFlyoutOpen || scrollY?.current > 0
            ? 'bg-white shadow-lg'
            : 'bg-transparent shadow-none'
        } z-100 relative py-4 transition-colors duration-300 data-[open]:bg-white`}
      >
        <nav aria-label="Top" className="mx-auto w-full px-0 lg:px-8">
          <div className="mx-auto">
            <div className="flex h-16 justify-center items-center w-full sm:px-0 mx-auto">
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="relative right-12 lg:hidden"
              >
                <span className="absolute -inset-0.5" />
                <span className="sr-only">Open menu</span>
                <Bars3Icon
                  aria-hidden="true"
                  className={`${
                    !isAnyFlyoutOpen && scrollY?.current === 0 && route
                      ? 'text-white'
                      : 'text-black'
                  } w-6 h-6`}
                />
              </button>

              {/* Logo */}
              <div className="flex lg:w-1/3 lg:ml-0">
                <a href="/">
                  <span className="sr-only">{title}</span>
                  {scrollY?.current > 0 || !route ? (
                    <BrandLogo color="black" className="w-40 mx-auto px-4" />
                  ) : (
                    <BrandLogo
                      color={!isAnyFlyoutOpen && route ? 'white' : 'black'}
                      className="w-40 mx-auto px-4"
                    />
                  )}
                </a>
              </div>

              {/* Flyout */}
              <PopoverGroup className="hidden w-2/3 mx-auto lg:flex lg:space-x-10 lg:justify-between">
                <div className="flex h-full w-full space-x-20">
                  {formatMenu?.map((menu) => (
                    <Popover key={menu.id}>
                      <div className="relative flex">
                        {/* Nav Links */}
                        <PopoverButton
                          onClick={() => handleFlyoutOpen(menu.title)}
                          className={`${
                            route && !isAnyFlyoutOpen && scrollY?.current === 0
                              ? 'text-white hover:border-white'
                              : 'text-black hover:border-black'
                          } flex items-center text-md tracking-wider font-medium uppercase border-b-2 border-transparent pt-px transition-colors duration-200 ease-out data-[open]:border-black data-[open]:text-black focus-visible:outline-none`}
                        >
                          <motion.span
                            variants={childVariants}
                            transition={{
                              ease: [0.1, 0.25, 0.3, 1],
                              duration: 0.4,
                            }}
                          >
                            {menu.title}
                          </motion.span>
                        </PopoverButton>
                      </div>
                      <PopoverPanel
                        transition
                        className="absolute inset-x-0 top-full text-sm text-gray-500 transition data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
                      >
                        <div
                          aria-hidden="true"
                          className="absolute inset-0 top-1/2 bg-white shadow"
                        />
                        <div className="relative bg-white text-black">
                          <div className="mx-auto px-36">
                            <div className="grid grid-cols-2 gap-x-8 gap-y-10 pt-10 pb-16">
                              {/* Upsell Section */}
                              {menu?.upsell && (
                                <div className="col-start-2 grid grid-cols-2 gap-x-8">
                                  {menu?.upsell?.map((item) => (
                                    <div
                                      key={uuid()}
                                      className="group relative text-sm"
                                    >
                                      <a
                                        href={item.product || item.collection}
                                        className="hover:opacity-75 transition-opacity duration-200"
                                      >
                                        <img
                                          alt={item.alt || item.cta}
                                          src={item.image}
                                          className="aspect-square w-full object-cover bg-gray-100 hover:opacity-75 transition-opacity duration-200"
                                        />
                                        <div className="mt-6 block uppercase tracking-widest font-medium text-black text-center">
                                          <span
                                            aria-hidden="true"
                                            className="absolute inset-0 z-10"
                                          />
                                          {item.cta}
                                        </div>
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* Products */}
                              <div className="row-start-1 grid grid-cols-3 gap-x-8 gap-y-10 text-sm">
                                {menu?.items &&
                                  [
                                    ...Array(Math.ceil(menu.items.length / 12)),
                                  ].map((_, columnIndex) => (
                                    <ul
                                      key={uuid()}
                                      aria-labelledby={`${menu.title}-column-${
                                        columnIndex + 1
                                      }`}
                                      className="mt-6 space-y-6 sm:mt-4 sm:space-y-4"
                                    >
                                      {menu.items
                                        .slice(
                                          columnIndex * 12,
                                          (columnIndex + 1) * 12,
                                        )
                                        .map((product) => (
                                          <li key={uuid()} className="flex">
                                            <a
                                              href={
                                                product.type === 'COLLECTION' ||
                                                product.title ===
                                                  'All Fragrances'
                                                  ? `/collections/${parseProductHandle(
                                                      product.to,
                                                    )}`
                                                  : `/products/${parseProductHandle(
                                                      product.to,
                                                    )}`
                                              }
                                              className="tracking-wider font-medium hover:text-gray-600 border-b-2 border-transparent hover:border-b-2 hover:border-gray-600 transition-all duration-200"
                                            >
                                              {product.title}
                                            </a>
                                          </li>
                                        ))}
                                    </ul>
                                  ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </PopoverPanel>
                    </Popover>
                  ))}
                  {/* Pages */}
                  <a
                    href="/our-story"
                    className={`${
                      route && !isAnyFlyoutOpen && scrollY?.current === 0
                        ? 'text-white'
                        : 'text-gray-900'
                    } flex items-center text-md tracking-wider font-medium border-b-2 border-transparent hover:border-b-2 hover:border-white uppercase transition-colors duration-200`}
                  >
                    Our Story
                  </a>
                </div>
              </PopoverGroup>

              {/* Account, Cart and Search */}
              <div className="flex items-center relative left-12 lg:left-0 lg:w-1/3">
                <div className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-end lg:space-x-6">
                  {/* Search */}
                  <div className="flex lg:ml-6">
                    <Search
                      className={`p-2 ${
                        route && !isAnyFlyoutOpen && scrollY?.current === 0
                          ? 'text-white placeholder:text-white/50'
                          : 'text-gray-900 placeholder:text-black/70'
                      } transition-colors duration-200`}
                    />
                  </div>

                  {/* Account */}
                  <a
                    href="/account"
                    className={`text-sm font-medium ${
                      route && !isAnyFlyoutOpen && scrollY?.current === 0
                        ? 'text-white'
                        : 'text-gray-900'
                    } transition-colors duration-200`}
                  >
                    <UserCircleIcon className="h-6 w-6" />
                  </a>
                  <span
                    aria-hidden="true"
                    className={`h-6 w-px ${
                      route && !isAnyFlyoutOpen && scrollY?.current === 0
                        ? 'bg-white'
                        : 'bg-gray-200'
                    } transition-colors duration-200`}
                  />
                </div>

                {/* Cart */}
                <div className="ml-4 flow-root lg:ml-6">
                  <button
                    type="button"
                    className="group -m-2 flex items-center p-2"
                    onClick={() => setCartOpen(true)}
                  >
                    <ShoppingBagIcon
                      className={`w-6 h-6 shrink-0 ${
                        route && !isAnyFlyoutOpen && scrollY?.current === 0
                          ? 'text-white'
                          : 'text-gray-900'
                      } transition-colors duration-200`}
                    />
                    <span
                      className={`ml-2 text-sm font-medium ${
                        route && !isAnyFlyoutOpen && scrollY?.current === 0
                          ? 'text-white'
                          : 'text-gray-900'
                      } transition-colors duration-200`}
                    >
                      <CartBadge />
                    </span>
                    <span className="sr-only">items in cart, view bag</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </motion.header>
      <CartDrawer cartOpen={cartOpen} setCartOpen={setCartOpen} />
    </div>
  );
}

function CartDrawer({cartOpen, setCartOpen}) {
  return (
    <Dialog open={cartOpen} onClose={setCartOpen} className="relative z-40">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/25 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 z-40 flex justify-end">
        <DialogPanel
          transition
          className="relative flex w-full lg:w-1/2 transform flex-col overflow-y-auto bg-white pb-12 shadow-xl transition duration-300 ease-in-out data-[closed]:translate-x-full"
        >
          <div className="flex px-4 py-6 justify-between items-center">
            <DialogTitle>
              <Heading
                as="span"
                size="lead"
                id="cart-contents"
                className="font-semibold uppercase text-md"
              >
                Your Shopping Bag
              </Heading>
            </DialogTitle>
            <button
              type="button"
              onClick={() => setCartOpen(false)}
              className="relative -m-2 inline-flex items-center justify-center rounded-md p-2 text-black"
            >
              <span className="absolute -inset-0.5" />
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="w-6 h-6" />
            </button>
          </div>
          <div className="grid border-t mt-4">
            <CartDetails layout="drawer" onClose={setCartOpen} />
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

function CartBadge() {
  const {totalQuantity} = useCart();

  if (totalQuantity < 1) {
    return null;
  }

  return (
    <div className="relative top-1 text-base font-semibold -ml-2">
      <span>{totalQuantity}</span>
    </div>
  );
}
